import React, { useState } from 'react';
import { FaExclamationTriangle, FaTimes, FaCalendarAlt, FaClock, FaBan, FaArrowRight, FaSpinner } from 'react-icons/fa';
import { EmployeeConflict, WorkAreaConflict, MovedShift } from '../services/PlantafelService';
import { pdebugError } from '../utils/plantafelDebug';

type ConflictType = 'employee' | 'workArea';

interface PlantafelConflictModalProps {
  isOpen: boolean;
  conflictType: ConflictType;
  employeeConflicts?: EmployeeConflict[];
  workAreaConflicts?: WorkAreaConflict[];
  entityName: string;
  shiftDate: string;
  onConfirm: () => void;
  onCancel: () => void;
  onPushConflicts?: () => Promise<MovedShift[]>;
}

export default function PlantafelConflictModal({
  isOpen,
  conflictType,
  employeeConflicts = [],
  workAreaConflicts = [],
  entityName,
  shiftDate,
  onConfirm,
  onCancel,
  onPushConflicts,
}: PlantafelConflictModalProps): React.JSX.Element | null {
  const [isPushing, setIsPushing] = useState(false);
  const [pushResult, setPushResult] = useState<MovedShift[] | null>(null);

  if (!isOpen) return null;

  const conflicts = conflictType === 'employee' ? employeeConflicts : workAreaConflicts;
  const conflictCount = conflicts.length;

  const formatDateTime = (isoString: string): string => {
    const dt = new Date(isoString);
    return dt.toLocaleString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTimeOnly = (isoString: string): string => {
    const dt = new Date(isoString);
    return dt.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getEmployeeConflictIcon = (type: EmployeeConflict['type']) => {
    switch (type) {
      case 'SHIFT':
        return <FaCalendarAlt size={14} color="#dc2626" />;
      case 'ABSENCE':
        return <FaBan size={14} color="#ea580c" />;
      case 'AVAILABILITY':
        return <FaClock size={14} color="#ca8a04" />;
      default:
        return <FaExclamationTriangle size={14} color="#dc2626" />;
    }
  };

  const getEmployeeConflictLabel = (type: EmployeeConflict['type']): string => {
    switch (type) {
      case 'SHIFT':
        return 'Bestehende Schicht';
      case 'ABSENCE':
        return 'Abwesenheit';
      case 'AVAILABILITY':
        return 'Keine Verfügbarkeit';
      default:
        return 'Konflikt';
    }
  };

  const handlePushConflicts = async () => {
    if (!onPushConflicts) return;

    setIsPushing(true);
    try {
      const movedShifts = await onPushConflicts();
      setPushResult(movedShifts);
    } catch (error) {
      pdebugError('Failed to push conflicts:', error);
    } finally {
      setIsPushing(false);
    }
  };

  const handleClose = () => {
    setPushResult(null);
    onCancel();
  };

  if (pushResult) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={handleClose}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '480px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '20px 24px',
              borderBottom: '1px solid #d1fae5',
              background: '#ecfdf5',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#d1fae5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FaArrowRight size={18} color="#059669" />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#065f46' }}>
                Jobs verschoben
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#059669' }}>
                {pushResult.length} Job{pushResult.length !== 1 ? 's' : ''} erfolgreich verschoben
              </p>
            </div>
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#065f46',
              }}
            >
              <FaTimes size={18} />
            </button>
          </div>

          <div style={{ padding: '16px 24px', maxHeight: '300px', overflow: 'auto' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {pushResult.map((shift, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    borderRadius: '8px',
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                  }}
                >
                  <FaCalendarAlt size={14} color="#059669" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>
                      {shift.task_title || `Shift #${shift.shift_id}`}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      <span>{formatTimeOnly(shift.old_start)}–{formatTimeOnly(shift.old_end)}</span>
                      <FaArrowRight size={10} color="#059669" />
                      <span style={{ color: '#059669', fontWeight: 500 }}>
                        {formatTimeOnly(shift.new_start)}–{formatTimeOnly(shift.new_end)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '16px 24px',
              borderTop: '1px solid #e5e7eb',
              background: '#fafafa',
            }}
          >
            <button
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none',
                background: '#059669',
                fontSize: '13px',
                fontWeight: 500,
                color: 'white',
                cursor: 'pointer',
              }}
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '480px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '20px 24px',
            borderBottom: '1px solid #fee2e2',
            background: '#fef2f2',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaExclamationTriangle size={18} color="#dc2626" />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#991b1b' }}>
              Konflikte erkannt
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#dc2626' }}>
              {entityName} hat {conflictCount} Konflikt{conflictCount !== 1 ? 'e' : ''}
            </p>
          </div>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#991b1b',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 24px', maxHeight: '300px', overflow: 'auto' }}>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666' }}>
            Für die Schicht am <strong>{shiftDate}</strong> wurden folgende Konflikte gefunden:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {conflictType === 'employee' && employeeConflicts.map((conflict, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '12px',
                  borderRadius: '8px',
                  background: '#fafafa',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ marginTop: '2px' }}>
                  {getEmployeeConflictIcon(conflict.type)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
                    {getEmployeeConflictLabel(conflict.type)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {formatDateTime(conflict.start_time)} – {formatDateTime(conflict.end_time)}
                  </div>
                  {conflict.description && (
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                      {conflict.description}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {conflictType === 'workArea' && workAreaConflicts.map((conflict, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '12px',
                  borderRadius: '8px',
                  background: '#fafafa',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ marginTop: '2px' }}>
                  <FaCalendarAlt size={14} color="#dc2626" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>
                    {conflict.task_title || `Shift #${conflict.shift_id}`}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {formatDateTime(conflict.start_time)} – {formatDateTime(conflict.end_time)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px' }}>
                    Überschneidung: {conflict.overlap_minutes} Min.
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '16px 24px',
            borderTop: '1px solid #e5e7eb',
            background: '#fafafa',
          }}
        >
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              borderRadius: '10px',
              border: '1px solid #d1d5db',
              background: 'white',
              fontSize: '13px',
              fontWeight: 500,
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            Abbrechen
          </button>

          {conflictType === 'workArea' && onPushConflicts && (
            <button
              onClick={handlePushConflicts}
              disabled={isPushing}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: 'none',
                background: '#7c3aed',
                fontSize: '13px',
                fontWeight: 500,
                color: 'white',
                cursor: isPushing ? 'not-allowed' : 'pointer',
                opacity: isPushing ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {isPushing ? (
                <>
                  <FaSpinner className="spin" size={12} />
                  Verschiebe...
                </>
              ) : (
                <>
                  <FaArrowRight size={12} />
                  Push conflicts
                </>
              )}
            </button>
          )}

          <button
            onClick={onConfirm}
            style={{
              padding: '10px 20px',
              borderRadius: '10px',
              border: 'none',
              background: '#dc2626',
              fontSize: '13px',
              fontWeight: 500,
              color: 'white',
              cursor: 'pointer',
            }}
          >
            Trotzdem zuweisen
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
}
